import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";

import {
  Box,
  Grid,
  Toolbar,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Pagination,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
} from "@mui/material";

import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import { CloudDownloadOutlined, KeyboardArrowLeftOutlined } from "@mui/icons-material";

import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";

function CriticalStockReport() {
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("role");
  const navigate = useNavigate();
  const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

  const [rawMaterials, setRawMaterials] = useState([]);
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(false);

  // Filters
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchText, setSearchText] = useState("");

  // Pagination
  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);

  const getRawMaterialsList = async () => {
    try {
      if (!outletData?._id) return;

      const params = {};
      if (selectedCategory) params.mainCategory = selectedCategory;
      if (searchText) params.searchRawMaterial = searchText;

      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletCurrentStock?outletId=${outletData._id}&limit=100`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        }
      );

      setCategories(response?.data?.data?.rawMaterialCategories || []);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const getStockTransactions = async () => {
    if (!outletData?._id) return;

    setLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.mainCategory = selectedCategory;

      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getCriticalStockReportForOutlet?outletId=${outletData._id}&limit=200`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        }
      );

      const report = response?.data?.data?.report;

      if (typeof report === "string" && report.includes("No Records")) {
        setRawMaterials([]);
      } else {
        setRawMaterials(Array.isArray(report) ? report : []);
      }

      setCurrentPage(1);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to fetch report", { autoClose: 3000 });
      setRawMaterials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRawMaterialsList();
    getStockTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Unique categories (same as your logic)
  const uniqueCategories = useMemo(() => {
    return (categories || []).filter(
      (category, index, self) =>
        index === self.findIndex((c) => c?.mainCategory?._id === category?.mainCategory?._id)
    );
  }, [categories]);

  // Client-side search (OrdersReport style)
  const filteredRows = useMemo(() => {
    const rows = Array.isArray(rawMaterials) ? rawMaterials : [];
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const name = (r?.rawMaterial?.name || "").toLowerCase();
      const cat = (r?.rawMaterial?.mainCategory?.name || "").toLowerCase();
      const sub = (r?.rawMaterial?.subCategory?.name || "").toLowerCase();
      const remark = (r?.remark || "").toLowerCase();
      const unit = (r?.rawMaterial?.unit || "").toLowerCase();
      return name.includes(q) || cat.includes(q) || sub.includes(q) || remark.includes(q) || unit.includes(q);
    });
  }, [rawMaterials, searchText]);

  const summary = useMemo(() => {
    const rows = Array.isArray(filteredRows) ? filteredRows : [];
    const outOfStock = rows.filter((r) => Number(r?.inStock || 0) <= 0).length;
    const total = rows.length;
    return { total, outOfStock };
  }, [filteredRows]);

  // Pagination
  const totalPages = Math.ceil((filteredRows?.length || 0) / itemsPerPage) || 1;
  const paginatedRows = filteredRows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (event, page) => setCurrentPage(page);

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const title = "Critical Items Stock Report";
    const textWidth = doc.getTextWidth(title);
    doc.setFontSize(16);
    doc.text(title, (pageWidth - textWidth) / 2, 20);

    doc.setFontSize(10);
    const outletName = outletData?.name || "Unknown Outlet";
    const outletAddress = outletData?.address || "No Address Available";
    const outletContact = `${outletData?.email || "No Email"}, ${outletData?.phone || "No Phone"}`;

    doc.text(`Outlet: ${outletName}`, 14, 30);
    doc.text(`Address: ${outletAddress}`, 14, 40);
    doc.text(`Contact: ${outletContact}`, 14, 50);

    const columns = [
      { header: "#", dataKey: "itemNo" },
      { header: "Raw Material", dataKey: "productName" },
      { header: "Category", dataKey: "category" },
      { header: "Sub Category", dataKey: "subcategory" },
      { header: "Stock", dataKey: "stock" },
      { header: "Unit", dataKey: "unit" },
      { header: "Remark", dataKey: "remark" },
      { header: "Status", dataKey: "status" },
    ];

    // Sort: category -> subcategory -> name
    const sortedMaterials = [...filteredRows].sort((a, b) => {
      const ca = (a?.rawMaterial?.mainCategory?.name || "").toLowerCase();
      const cb = (b?.rawMaterial?.mainCategory?.name || "").toLowerCase();
      const sa = (a?.rawMaterial?.subCategory?.name || "").toLowerCase();
      const sb = (b?.rawMaterial?.subCategory?.name || "").toLowerCase();
      const na = (a?.rawMaterial?.name || "").toLowerCase();
      const nb = (b?.rawMaterial?.name || "").toLowerCase();
      if (ca !== cb) return ca.localeCompare(cb);
      if (sa !== sb) return sa.localeCompare(sb);
      return na.localeCompare(nb);
    });

    const rows = sortedMaterials.map((m, index) => {
      const stock = Number(m?.inStock || 0);
      return {
        itemNo: index + 1,
        productName: m?.rawMaterial?.name || "N/A",
        category: m?.rawMaterial?.mainCategory?.name || "N/A",
        subcategory: m?.rawMaterial?.subCategory?.name || "N/A",
        stock: stock.toFixed(2),
        unit: m?.rawMaterial?.unit || "N/A",
        remark: m?.remark || "-",
        status: stock <= 0 ? "Out of Stock" : "In Stock",
      };
    });

    doc.autoTable({
      columns,
      body: rows,
      startY: 60,
      theme: "grid",
      styles: { halign: "center", fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 40 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 16 },
        5: { cellWidth: 14 },
        6: { cellWidth: 32 },
        7: { cellWidth: 22 },
      },
    });

    const today = new Date().toISOString().split("T")[0];
    doc.save(`${today}-Critical-Stock-Report.pdf`);
  };

  return (
    <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
      <Toolbar />

      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={11}>
          {/* Header Card */}
          <Card
            elevation={0}
            sx={{
              mt: 4,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack flexDirection={'row'}>
                  <KeyboardArrowLeftOutlined sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} fontSize="large" />
                  <Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                      Critical Items Stock Report
                    </Typography>
                    <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                      Filter by category, search items, then view or download the report.
                    </Typography>
                  </Box>
                </Stack>

                <Chip
                  icon={<FilterAltOutlined />}
                  label={`Records: ${summary.total}`}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Filters Card */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "#fff",
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center" justifyContent={"space-between"}>
                {/* Category */}
                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Category
                  </Typography>

                  <FormControl fullWidth>
                    <Select
                      value={selectedCategory}
                      onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        setCurrentPage(1);
                      }}
                      sx={{
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                      }}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>

                      {uniqueCategories?.map((c) => (
                        <MenuItem key={c?.mainCategory?._id} value={c?.mainCategory?._id}>
                          {c?.mainCategory?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Search */}
                <Grid item xs={12} md={5}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Search Raw Material
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Search by name / category / subcategory / unit / remark"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      setCurrentPage(1);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52, background: "#fff" },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8, textAlign: "center" }}>
                    Actions
                  </Typography>

                  <Stack direction="row" justifyContent={"space-between"} spacing={1}>
                    <Button
                      variant="contained"
                      disabled={loading}
                      onClick={getStockTransactions}
                      sx={{
                        height: 52,
                        width: "70%",
                        borderRadius: 2,
                        fontWeight: 800,
                        textTransform: "none",
                        backgroundColor: "#003049",
                        "&:hover": { backgroundColor: "#00263a" },
                      }}
                    >
                      {loading ? "Loading..." : "Get"}
                    </Button>

                    <CloudDownloadOutlined
                      onClick={() => filteredRows?.length > 0 && generatePDF()}
                      sx={{
                        fontSize: "35px",
                        cursor: filteredRows?.length > 0 ? "pointer" : "not-allowed",
                        color: filteredRows?.length > 0 ? "#003049" : "#94a3b8",
                        display: "flex",
                        alignSelf: "center",
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Content Card */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <CircularProgress />
                  <Typography sx={{ color: "#334155", fontWeight: 700 }}>Fetching Critical Stock...</Typography>
                </Box>
              ) : filteredRows?.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                    No critical stock reports to display
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Select a category (optional) and click “Get” to view results.
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Summary chips */}
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                          icon={<FilterAltOutlined />}
                          label={`Rows: ${summary.total}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                        <Chip
                          label={`Out of Stock: ${summary.outOfStock}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                      </Stack>

                      <Chip
                        label={`Outlet: ${outletData?.name || "N/A"}`}
                        sx={{
                          fontWeight: 900,
                          borderRadius: 2,
                          backgroundColor: "#003049",
                          color: "#fff",
                          "& .MuiChip-label": { color: "#fff" },
                        }}
                      />
                    </Stack>
                  </Box>

                  {/* Table */}
                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, maxHeight: "70vh", overflow: "auto" }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["No", "Raw Material", "Stock", "Unit", "Status"].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 900,
                                color: "#0f172a",
                                background: "#f1f5f9",
                                borderBottom: "1px solid #e2e8f0",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                              }}
                              align={h === "Raw Material" ? "left" : "center"}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {paginatedRows.map((m, index) => {
                          const stock = Number(m?.inStock || 0);
                          const isOut = stock <= 0;

                          return (
                            <TableRow
                              key={(m?.rawMaterial?._id || index) + "-" + index}
                              hover
                              sx={{
                                backgroundColor: isOut ? "#ffebee" : index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                "& td": { borderBottom: "1px solid #f1f5f9" },
                              }}
                            >
                              <TableCell align="center" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </TableCell>

                              <TableCell align="left">
                                <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#0f172a", textTransform: "capitalize" }}>
                                  {m?.rawMaterial?.name || "N/A"}
                                </Typography>

                                {/* Keep this as the secondary line instead of Category column */}
                                <Typography sx={{ fontSize: 12, color: "#64748b", textTransform: "capitalize" }}>
                                  {m?.rawMaterial?.mainCategory?.name || "N/A"} &gt; {m?.rawMaterial?.subCategory?.name || "N/A"}
                                </Typography>

                                {m?.remark ? (
                                  <Typography sx={{ fontSize: 11, color: "#475569", mt: 0.3, fontStyle: "italic" }}>
                                    {m.remark}
                                  </Typography>
                                ) : null}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 900, color: isOut ? "#b91c1c" : "#0f172a" }}>
                                {stock.toFixed(2)}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 700 }}>
                                {m?.rawMaterial?.unit || "N/A"}
                              </TableCell>

                              <TableCell align="center">
                                <Chip
                                  label={isOut ? "Out of Stock" : "In Stock"}
                                  size="small"
                                  sx={{
                                    fontWeight: 800,
                                    borderRadius: 2,
                                    backgroundColor: isOut ? "#ef4444" : "#16a34a",
                                    color: "#fff",
                                    "& .MuiChip-label": { color: "#fff" },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider />
                  <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CriticalStockReport;
